// The diff reader's cursors as data: what it keeps when the comparison
// changes under it (a new range or whitespace setting: the open file, the
// cursor's change, the scroll position; only opening a file moves the
// reader), and where the change, thread and line cursors move.
import type { Block } from '../engine/types';
import { isUnreviewed } from '../review/model';
import { rangeEnd, stopIndex, type LineCursor, type LineStop } from '../state/anchor';

/** What the reader showed: the open file in a comparison (any object identifying it, e.g. the diff model). */
export interface Shown {
  file: string | null;
  comparison: unknown;
}

/**
 * Whether showing `next` opens a file (scroll to its cursor block) rather
 * than re-showing the same file under a new comparison (leave scroll and
 * focus alone). The first file shown opens; so does any change of file.
 */
export function opensFile(prev: Shown | null, next: Shown): boolean {
  if (!next.file) return false;
  return !prev || prev.file !== next.file;
}

/** New-side line span of a block's changed rows (old-side lines when it only removes). */
function changedLines(b: Block): { lo: number; hi: number } | null {
  let lo = Infinity;
  let hi = -Infinity;
  let newSide = false;
  for (const r of b.rows) {
    if (r.kind === 'context') continue;
    if (r.new !== null) {
      newSide = true;
      lo = Math.min(lo, r.new);
      hi = Math.max(hi, r.new);
    }
  }
  if (newSide) return { lo, hi };
  // A pure deletion sits between the new-side lines around it.
  for (const r of b.rows) {
    const n = r.new ?? null;
    if (n !== null) {
      lo = Math.min(lo, n);
      hi = Math.max(hi, n);
    }
  }
  if (lo !== Infinity) return { lo, hi };
  for (const r of b.rows) {
    if (r.old === null) continue;
    lo = Math.min(lo, r.old);
    hi = Math.max(hi, r.old);
  }
  return lo === Infinity ? null : { lo, hi };
}

/** A change block where the reader draws it: the viewport y of its top and bottom edges. */
export interface PlacedChange {
  key: string;
  top: number;
  bottom: number;
}

/** How much of a change must show for the reader to be on it, in pixels: about a line. */
const SHOWS = 16;

/**
 * The change the cursor moves to when the reader was scrolled: none (null)
 * while any of the cursor's own change shows between `top` and `bottom`
 * (the reader's visible span), else the first change that shows there.
 * So the change a verb acts on is always one the reader can see; with no
 * change in sight the cursor stays where it was.
 */
export function cursorInView(changes: readonly PlacedChange[], cursor: string | null, top: number, bottom: number): string | null {
  const shows = (c: PlacedChange) => Math.min(c.bottom, bottom) - Math.max(c.top, top) >= Math.min(SHOWS, c.bottom - c.top);
  if (bottom - top < SHOWS) return null;
  const own = changes.find((c) => c.key === cursor);
  if (own && shows(own)) return null;
  return changes.find(shows)?.key ?? null;
}

/**
 * The change in `changes` (a file's change blocks under the new
 * comparison) that continues the cursor's `prev` block: the same block, or
 * one sharing any of its review-mark `changeKeys`, else the nearest change
 * by new-side line (ties go to the earlier one). Null when there are no
 * changes.
 */
export function carryCursor(prev: Block | null, changes: Block[]): string | null {
  if (!changes.length) return null;
  if (!prev) return changes[0].key;
  const same = changes.find((b) => b.key === prev.key);
  if (same) return same.key;
  const keys = new Set(prev.changeKeys);
  const shared = changes.find((b) => b.changeKeys.some((k) => keys.has(k)));
  if (shared) return shared.key;
  const at = changedLines(prev);
  if (!at) return changes[0].key;
  let best = changes[0].key;
  let bestD = Infinity;
  for (const b of changes) {
    const s = changedLines(b);
    if (!s) continue;
    const d = s.hi < at.lo ? at.lo - s.hi : s.lo > at.hi ? s.lo - at.hi : 0;
    if (d < bestD) {
      bestD = d;
      best = b.key;
    }
  }
  return best;
}

/**
 * The first item after (delta 1) or before (delta -1) `current` in `order`
 * that `keep` accepts (every item by default); null when none does before
 * the end. With `wrap`, it then continues from the other end and may come
 * back to `current` itself, so it finds an item whenever `keep` accepts any
 * and never loops. Without a current item in the order, the first (forward)
 * or last (back) item `keep` accepts. `current` itself need not pass
 * `keep` (the block just marked reviewed).
 */
export function nextInOrder<T>(
  order: readonly T[],
  current: T | null,
  delta: 1 | -1,
  keep: (item: T) => boolean = () => true,
  wrap = false,
): T | null {
  const i = current === null ? -1 : order.indexOf(current);
  for (let at = i < 0 ? (delta > 0 ? 0 : order.length - 1) : i + delta; at >= 0 && at < order.length; at += delta) {
    if (keep(order[at])) return order[at];
  }
  if (!wrap || i < 0) return null;
  for (let at = delta > 0 ? 0 : order.length - 1; at !== i + delta; at += delta) {
    if (keep(order[at])) return order[at];
  }
  return null;
}

/**
 * Where n (delta 1) or p (-1) lands on entering another file: its first
 * (last) change, or with `skipReviewed` its first (last) one not reviewed,
 * by that file's own flags (`reviewed`, its changes' in order, as
 * `DiffModel.changesReviewed` gives them; null when unknown, so none is
 * known reviewed). Null when there is none.
 */
export function enterFile<T>(changes: readonly T[], reviewed: readonly boolean[] | null, delta: 1 | -1, skipReviewed: boolean): T | null {
  return nextInOrder(changes, null, delta, skipReviewed ? (c) => !reviewed?.[changes.indexOf(c)] : undefined);
}

/** A changed file's review progress, as the diff model reports it (`exact` once its diff is computed). */
export interface FileProgress {
  total: number;
  done: number;
  exact: boolean;
}

/**
 * Whether n / p pass over a file without opening it: every one of its
 * changes is known to be reviewed. A file whose diff is not computed yet
 * (`exact` false) may hold unreviewed changes, so it is not skipped.
 */
export function skipsFile(p: FileProgress): boolean {
  return p.exact && !isUnreviewed(p);
}

/**
 * Whether n or p has somewhere to go: they wrap, so wherever a change is
 * not known to be reviewed, in the open file (`reviewed`: its changes'
 * review flags) or in another changed file (`files`).
 */
export function canSkipTo(reviewed: readonly boolean[], files: readonly FileProgress[]): boolean {
  return reviewed.some((r) => !r) || files.some((p) => !skipsFile(p));
}

/**
 * What n or p says when, wrapping included, it found no unreviewed change:
 * everything is reviewed, or (a file's diff not yet computed) none was found.
 */
export function queueEnd(reviewed: readonly boolean[], others: readonly FileProgress[]): string {
  return canSkipTo(reviewed, others) ? 'No unreviewed change found' : 'Everything reviewed';
}

/**
 * The stop the line cursor moves to (J/K: delta 1 or -1; 0 places it):
 * `delta` stops from the cursor's, kept within the stops. A cursor not on
 * any stop (none yet, or its line folded away) starts at the active
 * block's first stop; when that block shows none (it is folded), at the
 * first stop after it (K: the last before it) in `order`, the blocks'
 * display order; else the first. -1 when there are no stops.
 */
export function lineStopAfter(
  stops: LineStop[],
  cursor: LineCursor | null,
  activeBlock: string | null,
  delta: number,
  order: readonly string[] = [],
): number {
  if (!stops.length) return -1;
  const i = stopIndex(stops, cursor);
  if (i >= 0) return Math.max(0, Math.min(stops.length - 1, i + delta));
  const first = stops.findIndex((s) => s.block === activeBlock);
  if (first >= 0) return first;
  const index = new Map(order.map((key, i) => [key, i]));
  const at = activeBlock === null ? -1 : (index.get(activeBlock) ?? -1);
  if (at < 0) return 0;
  const pos = (s: LineStop) => index.get(s.block) ?? -1;
  const after = stops.findIndex((s) => pos(s) > at);
  let before = -1;
  stops.forEach((s, i) => {
    if (pos(s) < at) before = i;
  });
  if (delta < 0) return before >= 0 ? before : Math.max(after, 0);
  return after >= 0 ? after : Math.max(before, 0);
}

/**
 * The line on the anchor's side a range anchored at `anchor` reaches with
 * the line cursor at stop `at` (see `rangeEnd`); null when the anchor is
 * not on a stop or no stop back to it has a line on that side.
 */
export function rangeEndAt(stops: LineStop[], anchor: LineCursor, at: number): number | null {
  const from = stopIndex(stops, anchor);
  return from >= 0 ? rangeEnd(stops, anchor.side, from, at) : null;
}

/** The line `c` comments on in a block with no line cursor: its first added line, else its first removed one. */
export function commentLine(b: Block): LineCursor | null {
  const add = b.rows.find((r) => r.kind === 'add' && r.new !== null);
  if (add) return { side: 'RIGHT', line: add.new! };
  const del = b.rows.find((r) => r.kind === 'del' && r.old !== null);
  return del ? { side: 'LEFT', line: del.old! } : null;
}
